import { useState } from 'react';
import {
  makeStyles,
  Button,
  Toolbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import DateRangeIcon from '@material-ui/icons/DateRange';
import moment, { Moment } from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const useStyles = makeStyles(theme => ({
  headerToolbar: {
    padding: 0,
    minHeight: 'auto',
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
  },
  leftControls: {
    display: 'flex',
    alignItems: 'center',
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
  },
  timeRangeButtons: {
    display: 'flex',
    alignItems: 'center',
  },
  dateRangeButton: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  dateRangeText: {
    marginRight: theme.spacing(1),
  },
  dateTimePicker: {
    marginBottom: theme.spacing(2),
    width: '100%', // Faz o DateTimePicker ocupar toda a largura disponível
  },
  dateTimePickerField: {
    width: '100%', // Garante que o campo interno também ocupe toda a largura
  },
  dialogPaper: {
    backgroundColor: theme.palette.background.paper,
    width: '500px', // Define uma largura fixa maior para o diálogo
    maxWidth: '90vw', // Limita a largura máxima em telas pequenas
  },
  dialogContent: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 3), // Aumenta o padding para melhor espaçamento
  },
}));

type HeaderToolbarProps = {
  timeRange: string;
  changeTimeRange: (range: string) => void;
  disabled?: boolean;
};

export const HeaderToolbar = ({
  timeRange,
  disabled = false,
  changeTimeRange,
}: HeaderToolbarProps) => {
  const classes = useStyles();
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  // Format custom date range for display
  const getCustomRangeDisplay = () => {
    if (timeRange.includes('custom:')) {
      const [start, end] = timeRange.replace('custom:', '').split('|');
      return `${moment(start).format('MMM DD, HH:mm')} - ${moment(end).format(
        'MMM DD, HH:mm',
      )}`;
    }
    return '';
  };

  const handleOpenDatePicker = () => {
    setOpenDatePicker(true);
    // Initialize with current values if it's a custom range
    if (timeRange.includes('custom:')) {
      const [start, end] = timeRange.replace('custom:', '').split('|');
      setStartDate(moment(start));
      setEndDate(moment(end));
    } else {
      // Default to last 24 hours
      setEndDate(moment());
      setStartDate(moment().subtract(24, 'hours'));
    }
  };

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
  };

  const handleApplyCustomRange = () => {
    if (startDate && endDate) {
      const customRange = `custom:${startDate.toISOString()}|${endDate.toISOString()}`;
      changeTimeRange(customRange);
      handleCloseDatePicker();
    }
  };

  const isCustomRange = timeRange.includes('custom:');

  return (
    <Toolbar className={classes.headerToolbar}>
      <div className={classes.leftControls}>
        <div className={classes.timeRangeButtons}>
          <Button
            variant={timeRange === '1h' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => changeTimeRange('1h')}
            style={{ marginRight: 8 }}
            disabled={disabled}
          >
            1h
          </Button>
          <Button
            variant={timeRange === '3h' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => changeTimeRange('3h')}
            style={{ marginRight: 8 }}
            disabled={disabled}
          >
            3h
          </Button>
          <Button
            variant={timeRange === '6h' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => changeTimeRange('6h')}
            style={{ marginRight: 8 }}
            disabled={disabled}
          >
            6h
          </Button>
          <Button
            variant={timeRange === '12h' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => changeTimeRange('12h')}
            disabled={disabled}
          >
            12h
          </Button>
        </div>
      </div>

      <div className={classes.rightControls}>
        <Button
          variant={isCustomRange ? 'contained' : 'outlined'}
          color="primary"
          size="small"
          onClick={handleOpenDatePicker}
          disabled={disabled}
          startIcon={<DateRangeIcon />}
          className={classes.dateRangeButton}
        >
          {isCustomRange ? (
            <Typography variant="body2" className={classes.dateRangeText}>
              {getCustomRangeDisplay()}
            </Typography>
          ) : (
            'Período Personalizado'
          )}
        </Button>

        <Dialog
          open={openDatePicker}
          onClose={handleCloseDatePicker}
          PaperProps={{
            className: classes.dialogPaper,
          }}
          maxWidth="md"
        >
          <DialogTitle>
            Selecione Um Intervalo de Tempo Personalizado
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <div className={classes.dateTimePicker}>
                <DateTimePicker
                  label="Data e Hora de Início"
                  value={startDate}
                  onChange={(newValue: Moment | null) => {
                    setStartDate(newValue);
                  }}
                  className={classes.dateTimePickerField}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                    },
                  }}
                />
              </div>
              <div className={classes.dateTimePicker}>
                <DateTimePicker
                  label="Data e Hora de Término"
                  value={endDate}
                  onChange={(newValue: Moment | null) => {
                    setEndDate(newValue);
                  }}
                  className={classes.dateTimePickerField}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                    },
                  }}
                />
              </div>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDatePicker} color="primary">
              Cancelar
            </Button>
            <Button
              onClick={handleApplyCustomRange}
              color="primary"
              variant="contained"
            >
              Aplicar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Toolbar>
  );
};
